import React, { useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * FormKit — ชิ้นส่วนของหน้าตั้งค่า
 *
 * กลุ่มหน้าจอที่ 3 (กติกาคะแนน · Tier · สิทธิพิเศษ · ของรางวัล · คูปอง · แบนเนอร์)
 * เป็นหน้า "ตั้งค่า" ไม่ใช่หน้า "คิวงาน" จึงต้องการชิ้นส่วนที่ต่างจากตาราง:
 *
 *   1. ทุกหน้ามีค่าที่แก้แล้วยังไม่บันทึก  → SaveBar ต้องบอกจำนวนที่แก้ และกันการออกจากหน้า
 *   2. ทุกหน้าต้องบอกผลกระทบก่อนบันทึก    → Callout + ImpactList
 *   3. ทุกหน้ามีแถบสรุปตัวเลขด้านบน       → SummaryStrip (เดิมเขียนซ้ำในทุกหน้าจอ)
 *   4. class ของ input เดิมถูก copy ไปทุกไฟล์ → รวมไว้ที่นี่ที่เดียว
 *
 * ⚠ ไม่มีค่าคงที่ทางธุรกิจในไฟล์นี้ ทุกค่ามาจาก props (หลักการ No-code ข้อ 3.1 ของ SRS)
 */

/* ------------------------------------------------------------- class กลาง */

const controlBase =
  'rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] font-[family-name:var(--font-thai)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2 disabled:bg-[var(--bg-disabled)] disabled:text-[var(--text-disabled)] disabled:cursor-not-allowed';

export const inputCls = cn(
  controlBase,
  'min-h-[var(--hit-control)] px-[var(--space-2)] text-[length:var(--text-form)]',
);

export const inputLgCls = cn(
  controlBase,
  'min-h-[var(--hit-primary)] px-[var(--space-25)] text-[length:var(--text-form-lg)]',
);

export const textareaCls = cn(
  controlBase,
  'min-h-[76px] resize-y p-[var(--space-2)] text-[length:var(--text-form)] leading-[var(--leading-thai)]',
);

export const primaryBtnCls =
  'inline-flex min-h-[var(--hit-primary)] items-center justify-center gap-[var(--space-15)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-5)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-white hover:bg-[var(--blue-800)] disabled:cursor-not-allowed disabled:bg-[var(--bg-disabled)] disabled:text-[var(--text-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2';

export const secondaryBtnCls =
  'inline-flex min-h-[var(--hit-primary)] items-center justify-center gap-[var(--space-15)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] disabled:cursor-not-allowed disabled:text-[var(--text-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2';

export const ghostBtnCls =
  'inline-flex min-h-[var(--hit-control)] items-center gap-1 rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--blue-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]';

/* ------------------------------------------------------------ แสดงค่าอ่าน */

/** คู่ label + ค่า สำหรับแสดงข้อมูลที่แก้ไม่ได้ในหน้านี้ */
export const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  hint?: React.ReactNode;
  tone?: 'default' | 'warning' | 'error';
}> = ({ label, children, hint, tone = 'default' }) => (
  <div className="flex min-w-0 flex-col gap-[var(--space-05)]">
    <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{label}</span>
    <span
      className="text-[length:var(--text-form-lg)] leading-[var(--leading-form)] font-[var(--weight-medium)]"
      style={{ color: tone === 'error' ? 'var(--error-text)' : tone === 'warning' ? 'var(--warning-text)' : 'var(--ink-900)' }}
    >
      {children}
    </span>
    {hint && <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-600)]">{hint}</span>}
  </div>
);

/* --------------------------------------------------------- แถวของฟอร์ม */

export interface FormRowProps {
  label: string;
  /** คำอธิบายใต้ label — ใช้บอก "ค่านี้มีผลกับอะไร" ไม่ใช่บอกวิธีกรอก */
  help?: React.ReactNode;
  /** ค่าเดิมก่อนแก้ — แสดงเมื่อมีการเปลี่ยนแปลงที่ยังไม่บันทึก */
  previousValue?: React.ReactNode;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
  /**
   * จองพื้นที่ข้อความใต้ฟิลด์ไว้ล่วงหน้าหนึ่งบรรทัด
   * ใช้กับฟอร์มที่มี error หรือ "ค่าเดิม" โผล่ระหว่างพิมพ์ เพื่อไม่ให้ฟอร์มกระโดด
   */
  reserveMessageSpace?: boolean;
}

export const FormRow: React.FC<FormRowProps> = ({
  label, help, previousValue, error, required, htmlFor, children, className,
  reserveMessageSpace = false,
}) => {
  const hasPrevious = previousValue !== undefined && previousValue !== null;

  return (
    <div className={cn('flex flex-col gap-[var(--space-1)]', className)}>
      <label htmlFor={htmlFor} className="flex items-center gap-1 text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-900)]">
        {label}
        {required && <span aria-hidden="true" className="text-[var(--error-text)]">*</span>}
        {required && <span className="sr-only">จำเป็น</span>}
      </label>
      {help && (
        <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-600)]">{help}</span>
      )}
      {children}

      {/**
       * ช่องข้อความใต้ฟิลด์ — แสดงได้ทีละอย่าง และจองพื้นที่ไว้ได้
       *
       * ⚠️ เดิมแสดง "ค่าเดิม" กับ error ซ้อนกันได้สองบรรทัด และไม่จองที่ไว้
       *    พอผู้ใช้พิมพ์ตัวอักษรแรก บรรทัด "ค่าเดิม" โผล่ขึ้นมาแล้ว**ดันทุกฟิลด์
       *    ที่อยู่ข้างล่างลง 25px** ทำให้ฟอร์มกระโดดทุกครั้งที่แก้ค่า
       *    (วัดจริงในหน้ากติกาคะแนน: พิมพ์ 1 ตัว ดัน 7 ช่องพร้อมกัน)
       *
       *    แก้สองอย่าง
       *    1. error มาก่อนเสมอ ไม่แสดงพร้อม "ค่าเดิม" → สูงไม่เกินหนึ่งบรรทัด
       *    2. reserveMessageSpace จองความสูงหนึ่งบรรทัดไว้ตั้งแต่แรก
       *       ใช้กับฟอร์มที่ผู้ใช้แก้ค่าไปมา ไม่เปิดทั้งระบบเพราะฟอร์มที่ไม่มี
       *       ข้อความใต้ฟิลด์จะเสียที่ว่างเปล่า ๆ
       */}
      {(error || hasPrevious || reserveMessageSpace) && (
        <span
          className="flex items-start gap-1 text-[length:var(--text-table-sm)] leading-[var(--leading-thai)]"
          style={reserveMessageSpace
            ? { minHeight: 'calc(var(--text-table-sm) * var(--leading-thai))' }
            : undefined}
        >
          {error ? (
            <span role="alert" className="flex items-start gap-1 text-[var(--error-text)]">
              <Icon name="error" size={14} filled />
              {error}
            </span>
          ) : hasPrevious ? (
            <span className="flex items-center gap-1 text-[var(--ink-700)]">
              <Icon name="history" size={14} />
              ค่าเดิม: <span className="font-[var(--weight-medium)]">{previousValue}</span>
            </span>
          ) : null}
        </span>
      )}
    </div>
  );
};

/* ------------------------------------------------------------- ช่วงตัวเลข */

/** input ตัวเลขพร้อมหน่วยต่อท้าย — หน่วยอยู่ในกรอบเดียวกันเพื่อไม่ให้อ่านผิดหน่วย */
export const NumberField: React.FC<{
  value: number | '';
  onChange: (v: number | '') => void;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  width?: number;
  id?: string;
  disabled?: boolean;
  ariaLabel?: string;
}> = ({ value, onChange, unit, min, max, step = 1, width = 160, id, disabled, ariaLabel }) => (
  <span
    className="inline-flex items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] pr-[var(--space-25)] focus-within:ring-[3px] focus-within:ring-[var(--focus-ring-color)] focus-within:ring-offset-2"
    style={{ width }}
  >
    <input
      id={id}
      type="number"
      inputMode="numeric"
      aria-label={ariaLabel}
      value={value}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      /* เลือกค่าทั้งช่องเมื่อโฟกัส — ไม่งั้นพิมพ์ต่อท้าย 0 ที่ค้างอยู่ กลายเป็น 50 ทั้งที่ตั้งใจพิมพ์ 5
         แล้วผู้ใช้ต้องย้อนไปลบ 0 ทิ้งเองทุกครั้ง */
      onFocus={(e) => e.currentTarget.select()}
      className="bo-num min-h-[var(--hit-control)] w-full min-w-0 rounded-[var(--radius-sm)] border-0 bg-transparent px-[var(--space-2)] text-[length:var(--text-form-lg)] text-[var(--ink-900)] outline-none disabled:text-[var(--text-disabled)]"
    />
    {unit && <span className="shrink-0 text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{unit}</span>}
  </span>
);

/* --------------------------------------------------------- checkbox แถวยาว */

/** checkbox ที่มีคำอธิบายผลของการเปิด/ปิด — ใช้เมื่อ toggle เปล่าไม่พอ */
export const CheckRow: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: React.ReactNode;
  disabled?: boolean;
  tone?: 'default' | 'warning';
}> = ({ checked, onChange, label, description, disabled, tone = 'default' }) => (
  <label
    className={cn(
      'flex cursor-pointer items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] p-[var(--space-25)]',
      tone === 'warning' ? 'bg-[var(--warning-bg)]' : 'bg-[var(--blue-050)]',
      disabled && 'cursor-not-allowed opacity-60',
    )}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-[3px] h-4 w-4 shrink-0 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
    />
    <span className="min-w-0">
      <span className="block text-[length:var(--text-form-lg)] text-[var(--ink-900)]">{label}</span>
      {description && (
        <span className="block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">{description}</span>
      )}
    </span>
  </label>
);

/* --------------------------------------------------------------- ส่วนของหน้า */

/**
 * ⚠️ `requirement` **ไม่แสดงบนหน้าจอแล้ว** — รหัสข้อกำหนด (FR-xx) เป็นภาษาภายในทีม
 *    ผู้ใช้งานจริงอ่านไม่รู้เรื่องและทำให้ดูเหมือนระบบยังอยู่ระหว่างทำ
 *    ยังรับค่าไว้เพื่อให้โค้ดบอกได้ว่าส่วนนี้มาจากข้อกำหนดข้อไหน
 */
export interface FormSectionProps {
  title: string;
  /** อธิบายว่าค่าในส่วนนี้กระทบอะไร — ไม่ใช่คำอธิบายวิธีกรอก */
  description?: React.ReactNode;
  /** รหัสข้อกำหนดที่ส่วนนี้รองรับ เช่น 'FR-ADM-24' — แสดงเพื่อให้ตรวจกับ SRS ได้ */
  requirement?: string;
  /** ของที่วางมุมขวาบนของหัวข้อ เช่น ปุ่มเพิ่มรายการ */
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export const FormSection: React.FC<FormSectionProps> = ({
  title, description, action, children, className,
}) => (
  <section
    className={cn(
      'flex flex-col gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-4)]',
      className,
    )}
  >
    <header className="flex flex-wrap items-start justify-between gap-[var(--space-3)]">
      <div className="flex min-w-0 flex-col gap-[var(--space-05)]">
        <h2 className="m-0 flex items-center gap-[var(--space-2)] text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-form)] text-[var(--ink-900)]">
          {title}
        </h2>
        {description && (
          <p className="m-0 max-w-[92ch] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
            {description}
          </p>
        )}
      </div>
      {action}
    </header>
    {children}
  </section>
);

/* ------------------------------------------------------------------ Callout */

export type CalloutTone = 'info' | 'warning' | 'error' | 'success';

const CALLOUT: Record<CalloutTone, { bg: string; border: string; text: string; icon: string }> = {
  info: { bg: 'var(--info-bg)', border: 'var(--info-border)', text: 'var(--info-text)', icon: 'info' },
  warning: { bg: 'var(--warning-bg)', border: 'var(--warning-border)', text: 'var(--warning-text)', icon: 'warning' },
  error: { bg: 'var(--error-bg)', border: 'var(--error-border)', text: 'var(--error-text)', icon: 'report' },
  success: { bg: 'var(--success-bg)', border: 'var(--success-border)', text: 'var(--success-text)', icon: 'check_circle' },
};

/** กล่องข้อความเตือน — ไอคอนมาคู่กับสีทุกครั้ง (กฎ "ห้ามสื่อด้วยสีเดียว") */
export const Callout: React.FC<{
  tone?: CalloutTone;
  title?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ tone = 'info', title, children, action, className }) => {
  const p = CALLOUT[tone];
  return (
    <div
      role={tone === 'error' || tone === 'warning' ? 'alert' : undefined}
      className={cn('flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border p-[var(--space-25)]', className)}
      style={{ background: p.bg, borderColor: p.border }}
    >
      <Icon name={p.icon} size={19} filled style={{ color: p.text, flexShrink: 0, marginTop: 1 }} />
      <div className="min-w-0 flex-1">
        {title && (
          <p className="m-0 text-[length:var(--text-form)] font-[var(--weight-semibold)] leading-[var(--leading-form)]" style={{ color: p.text }}>
            {title}
          </p>
        )}
        <div className="max-w-[100ch] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">{children}</div>
      </div>
      {action}
    </div>
  );
};

/* ------------------------------------------------------------ SummaryStrip */

export interface SummaryStat {
  label: string;
  value: string;
  icon: string;
  /** สีของไอคอนและตัวเลข — ต้องมีคำกำกับที่ label อยู่แล้ว สีเป็นตัวช่วยเท่านั้น */
  tone?: 'default' | 'brand' | 'warning' | 'error' | 'success';
  /** คำอธิบายเพิ่มเมื่อตัวเลขนี้ต้องตีความ เช่น "สูงผิดปกติ" */
  note?: string;
}

const TONE_COLOR: Record<NonNullable<SummaryStat['tone']>, string> = {
  default: 'var(--ink-700)',
  brand: 'var(--blue-700)',
  warning: 'var(--warning-text)',
  error: 'var(--error-text)',
  success: 'var(--success-text)',
};

/** แถบตัวเลขสรุปใต้ Toolbar — เดิมเขียนซ้ำในทุกหน้าจอ */
export const SummaryStrip: React.FC<{ stats: SummaryStat[]; className?: string }> = ({ stats, className }) => (
  <div
    className={cn(
      'flex flex-wrap items-center gap-x-[var(--space-5)] gap-y-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--blue-050)] px-[var(--space-4)] py-[var(--space-2)]',
      className,
    )}
  >
    {stats.map((s) => (
      <span key={s.label} className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
        <Icon name={s.icon} size={18} style={{ color: TONE_COLOR[s.tone ?? 'default'] }} />
        {s.label}
        <strong
          className="bo-num text-[length:var(--text-section)] font-[var(--weight-bold)]"
          style={{ textAlign: 'left', color: s.tone === 'default' || !s.tone ? 'var(--ink-900)' : TONE_COLOR[s.tone] }}
        >
          {s.value}
        </strong>
        {s.note && (
          <span className="text-[length:var(--text-table-sm)]" style={{ color: TONE_COLOR[s.tone ?? 'default'] }}>
            {s.note}
          </span>
        )}
      </span>
    ))}
  </div>
);

/* ---------------------------------------------------------------- SaveBar */

export interface SaveBarProps {
  /** จำนวนช่องที่แก้แล้วยังไม่บันทึก — 0 = แถบอยู่ในสถานะเงียบ */
  changeCount: number;
  /** รายชื่อสิ่งที่เปลี่ยน — เปิดดูได้ก่อนกดบันทึก ไม่ต้องเดา */
  changes?: Array<{ field: string; from: string; to: string }>;
  onSave: () => void;
  onDiscard: () => void;
  saveLabel?: string;
  /** ข้อความที่ต้องอ่านก่อนบันทึก เช่น จำนวนคนที่กระทบ */
  impact?: React.ReactNode;
  /** ปิดปุ่มบันทึกพร้อมเหตุผลที่ปิด */
  blockedReason?: string;
  busy?: boolean;
}

export const SaveBar: React.FC<SaveBarProps> = ({
  changeCount, changes, onSave, onDiscard, saveLabel = 'บันทึกการเปลี่ยนแปลง', impact, blockedReason, busy,
}) => {
  const [openList, setOpenList] = useState(false);
  const dirty = changeCount > 0;
  return (
    <div
      className="sticky bottom-0 z-[var(--z-sticky)] flex flex-col gap-[var(--space-2)] border-t px-[var(--space-4)] py-[var(--space-25)]"
      style={{
        background: dirty ? 'var(--blue-100)' : 'var(--surface-0)',
        borderColor: dirty ? 'var(--blue-200)' : 'var(--line-200)',
        boxShadow: 'var(--elevation-sticky)',
      }}
    >
      {dirty && openList && changes && changes.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-[var(--space-1)] rounded-[var(--radius-sm)] bg-[var(--surface-0)] p-[var(--space-25)]">
          {changes.map((c) => (
            <li key={c.field} className="flex flex-wrap items-baseline gap-[var(--space-2)] text-[length:var(--text-form)] leading-[var(--leading-form)]">
              <span className="text-[var(--ink-700)]">{c.field}</span>
              <span className="text-[var(--ink-600)] line-through">{c.from}</span>
              <Icon name="arrow_forward" size={14} className="text-[var(--ink-600)]" />
              <span className="font-[var(--weight-semibold)] text-[var(--ink-900)]">{c.to}</span>
            </li>
          ))}
        </ul>
      )}
      {dirty && impact && (
        <p className="m-0 flex items-start gap-[var(--space-15)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
          <Icon name="groups" size={17} filled className="mt-[var(--space-05)] shrink-0 text-[var(--blue-700)]" />
          {impact}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-[var(--space-3)]">
        <span aria-live="polite" className="flex items-center gap-[var(--space-15)] text-[length:var(--text-form)] text-[var(--ink-900)]">
          {dirty ? (
            <>
              <Icon name="edit_note" size={19} filled className="text-[var(--blue-700)]" />
              แก้ไปแล้ว <strong className="bo-num" style={{ textAlign: 'left' }}>{changeCount.toLocaleString('th-TH')}</strong> ช่อง ยังไม่บันทึก
            </>
          ) : (
            <>
              <Icon name="check_circle" size={19} filled className="text-[var(--success-text)]" />
              บันทึกไว้ครบแล้ว
            </>
          )}
        </span>
        {dirty && changes && changes.length > 0 && (
          <button type="button" onClick={() => setOpenList((v) => !v)} className={ghostBtnCls} aria-expanded={openList}>
            <Icon name={openList ? 'expand_less' : 'expand_more'} size={16} />
            {openList ? 'ซ่อนรายการที่แก้' : 'ดูรายการที่แก้'}
          </button>
        )}
        <span className="flex-1" />
        {blockedReason && dirty && (
          <span className="flex items-center gap-1 text-[length:var(--text-table-sm)] text-[var(--error-text)]">
            <Icon name="error" size={15} filled />
            {blockedReason}
          </span>
        )}
        <button type="button" onClick={onDiscard} disabled={!dirty || busy} className={secondaryBtnCls}>
          ยกเลิกการแก้ไข
        </button>
        <button type="button" onClick={onSave} disabled={!dirty || busy || Boolean(blockedReason)} className={primaryBtnCls}>
          {saveLabel}
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------- รายการผลกระทบ */

/** ตาราง "ค่านี้กระทบใคร ตั้งแต่เมื่อไร" — ใช้ก่อนยืนยันการเปลี่ยนกติกา */
export const ImpactList: React.FC<{
  items: Array<{ who: string; count: number | null; unit?: string; when: string; detail?: string }>;
  className?: string;
}> = ({ items, className }) => (
  <ul className={cn('m-0 flex list-none flex-col gap-[var(--space-2)] p-0', className)}>
    {items.map((it) => (
      <li key={it.who} className="flex flex-wrap items-baseline gap-x-[var(--space-3)] gap-y-[var(--space-05)] border-b border-[var(--line-200)] pb-[var(--space-2)] last:border-0 last:pb-0">
        <span className="min-w-[200px] flex-1 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">{it.who}</span>
        <span className="bo-num min-w-[104px] text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--ink-900)]">
          {it.count === null ? '—' : `${it.count.toLocaleString('th-TH')} ${it.unit ?? 'คน'}`}
        </span>
        <span className="flex min-w-[168px] items-center gap-1 text-[length:var(--text-form)] text-[var(--ink-700)]">
          <Icon name="event" size={15} />
          {it.when}
        </span>
        {it.detail && (
          <span className="w-full text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-600)]">{it.detail}</span>
        )}
      </li>
    ))}
  </ul>
);
